import { useCallback, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { useUserSettingsUi, useUserSettingsUiMutation } from '../../app/query-hooks/use-user-settings-ui';
import { resolveJsaLevelComparisonShowProficiency } from '../url-tile-payloads';

export type ComparisonProficiencySettings = {
    readonly highlightProficiency: boolean;
    readonly showProficiency: boolean;
    readonly onHighlightProficiencyChange: (highlightProficiency: boolean) => void;
    readonly onShowProficiencyChange: (showProficiency: boolean) => void;
};

// Both comparison tiles share the persisted level-comparison toggles; the tile URL wins over the stored value.
export const useComparisonProficiencySettings = ({
    tileShowProficiency,
    updateTileProficiency,
}: {
    readonly tileShowProficiency: boolean | undefined;
    readonly updateTileProficiency: (showProficiency: boolean) => void;
}): ComparisonProficiencySettings => {
    const { validatingCobrainerApiFetcher } = useApplicationContext();
    const { data: userSettings } = useUserSettingsUi(validatingCobrainerApiFetcher, { enabled: true });
    const { userSettingsUiMutationOptimistic } = useUserSettingsUiMutation(validatingCobrainerApiFetcher);
    const { showErrorSnackbar } = useSnackbars();
    const [isProficiencyVisible, setIsProficiencyVisible] = useState(tileShowProficiency ?? true);
    const [isProficiencyHighlightVisible, setIsProficiencyHighlightVisible] = useState(false);

    const showProficiency = resolveJsaLevelComparisonShowProficiency(
        tileShowProficiency,
        userSettings?.jobArchitectureLevelComparisonShowProficiency,
        isProficiencyVisible,
    );
    const highlightProficiency =
        userSettings?.jobArchitectureLevelComparisonHighlightProficiency ?? isProficiencyHighlightVisible;

    const onShowProficiencyChange = useCallback(
        (nextVisibility: boolean): void => {
            const previousVisibility = showProficiency;

            setIsProficiencyVisible(nextVisibility);
            updateTileProficiency(nextVisibility);
            userSettingsUiMutationOptimistic.mutate(
                { jobArchitectureLevelComparisonShowProficiency: nextVisibility },
                {
                    onError: (mutationError) => {
                        setIsProficiencyVisible(previousVisibility);
                        updateTileProficiency(previousVisibility);
                        showErrorSnackbar(mutationError);
                    },
                },
            );
        },
        [showErrorSnackbar, showProficiency, updateTileProficiency, userSettingsUiMutationOptimistic],
    );

    const onHighlightProficiencyChange = useCallback(
        (nextVisibility: boolean): void => {
            const previousVisibility = highlightProficiency;

            setIsProficiencyHighlightVisible(nextVisibility);
            userSettingsUiMutationOptimistic.mutate(
                { jobArchitectureLevelComparisonHighlightProficiency: nextVisibility },
                {
                    onError: (mutationError) => {
                        setIsProficiencyHighlightVisible(previousVisibility);
                        showErrorSnackbar(mutationError);
                    },
                },
            );
        },
        [highlightProficiency, showErrorSnackbar, userSettingsUiMutationOptimistic],
    );

    return { highlightProficiency, onHighlightProficiencyChange, onShowProficiencyChange, showProficiency };
};
